import React, { useState, useEffect } from 'react';
import { ackHealth, unackHealth, isAcked, attachAckSig, resolvedDisplay, mergeServerAcks, checkEngineResolved } from '../healthAck';
import { getDevices, getPositions } from '../traccar';
import { describeAttribute, GROUP_ORDER } from '../obdCatalog';
import { dtcCodesFromAttrs } from '../vehicleAlerts';
import { playOpen, playClose } from '../clickSound';

const GROUP_ICONS = {
  Engine: '⚙️', Fuel: '⛽', 'Health & Alerts': '🩺', Temperatures: '🌡️',
  Electrical: '🔋', 'Speed & Distance': '📏', 'GPS Quality': '📡',
  'Device & Network': '📶', 'Vehicle Identity': '🪪', Other: '📦',
};

// ---- OBD-II health assessment: turns raw readings into verdicts ----
const HEALTH_COLORS = {
  good: { bg: '#f0fdf4', border: '#22c55e', text: '#15803d' },
  warn: { bg: '#fffbeb', border: '#f59e0b', text: '#b45309' },
  bad: { bg: '#fef2f2', border: '#ef4444', text: '#b91c1c' },
  unknown: { bg: '#f8fafc', border: '#cbd5e1', text: '#64748b' },
};

function assessHealth(a = {}) {
  const checks = [];

  // Check engine / fault codes
  if (a.io30 != null) {
    const codes = dtcCodesFromAttrs(a); // [{ code, meaning }] — empty if only a count came through
    checks.push(a.io30 === 0
      ? { icon: '✅', title: 'Check Engine', verdict: 'No fault codes', detail: 'The engine computer reports no active problems.', level: 'good' }
      : {
        icon: '🚨', title: 'Check Engine', verdict: `${a.io30} fault code(s) active`,
        detail: codes.length ? 'Tap to see the codes.' : 'Read codes with a scanner or enable code reporting.',
        level: 'bad', codes,
      });
  }

  // Coolant temperature (only meaningful with the engine running)
  if (a.io32 != null) {
    const t = a.io32;
    checks.push(
      a.ignition !== true ? { icon: '⏸️', title: 'Engine Temperature', verdict: 'Engine off', detail: 'Live temperature reads when the engine is running.', level: 'unknown' }
      : t > 115 ? { icon: '🔥', title: 'Engine Temperature', verdict: `Overheating — ${t} °C`, detail: 'Stop the vehicle. Normal range is 85–105 °C.', level: 'bad' }
      : t > 105 ? { icon: '🌡️', title: 'Engine Temperature', verdict: `Running hot — ${t} °C`, detail: 'Above the normal 85–105 °C range. Keep an eye on it.', level: 'warn' }
      : t < 70 ? { icon: '❄️', title: 'Engine Temperature', verdict: `Warming up — ${t} °C`, detail: 'Engine has not reached operating temperature yet.', level: 'unknown' }
      : { icon: '✅', title: 'Engine Temperature', verdict: `Normal — ${t} °C`, detail: 'Within the healthy 85–105 °C operating range.', level: 'good' }
    );
  }

  // Battery & charging
  if (a.power != null) {
    const v = a.power;
    const running = !!a.ignition;
    checks.push(
      running
        ? (v >= 13.2 ? { icon: '✅', title: 'Battery & Charging', verdict: `Charging — ${v.toFixed(1)} V`, detail: 'Alternator is charging normally (13.2–14.8 V expected while running).', level: 'good' }
          : v >= 12.4 ? { icon: '⚠️', title: 'Battery & Charging', verdict: `Weak charging — ${v.toFixed(1)} V`, detail: 'Below 13.2 V with the engine on. Alternator or belt may need attention.', level: 'warn' }
          : { icon: '🪫', title: 'Battery & Charging', verdict: `Not charging — ${v.toFixed(1)} V`, detail: 'Voltage too low while running. Have the charging system checked.', level: 'bad' })
        : (v >= 12.4 ? { icon: '✅', title: 'Battery (engine off)', verdict: `Healthy — ${v.toFixed(1)} V`, detail: 'A rested battery above 12.4 V is in good shape.', level: 'good' }
          : v >= 11.8 ? { icon: '⚠️', title: 'Battery (engine off)', verdict: `Getting low — ${v.toFixed(1)} V`, detail: 'Below 12.4 V at rest. Battery may be aging or drained.', level: 'warn' }
          : { icon: '🪫', title: 'Battery (engine off)', verdict: `Very low — ${v.toFixed(1)} V`, detail: 'Risk of not starting. Charge or replace the battery.', level: 'bad' })
    );
  }

  // Fuel level
  if (a.io48 != null) {
    const f = a.io48;
    checks.push(
      f < 10 ? { icon: '⛽', title: 'Fuel', verdict: `Critical — ${f}%`, detail: 'Nearly empty.', level: 'bad' }
      : f < 25 ? { icon: '⛽', title: 'Fuel', verdict: `Low — ${f}%`, detail: 'Plan a fuel stop soon.', level: 'warn' }
      : { icon: '✅', title: 'Fuel', verdict: `${f}% in tank`, detail: 'Fuel level is fine.', level: 'good' }
    );
  }

  // ECU voltage sanity
  if (a.io51 != null) {
    const v = a.io51 / 1000;
    checks.push(
      v >= 11.5 ? { icon: '✅', title: 'Vehicle Computer (ECU)', verdict: `Stable — ${v.toFixed(2)} V`, detail: 'The engine computer is receiving healthy power.', level: 'good' }
      : { icon: '⚠️', title: 'Vehicle Computer (ECU)', verdict: `Low supply — ${v.toFixed(2)} V`, detail: 'ECU voltage below 11.5 V can cause electrical gremlins.', level: 'warn' }
    );
  }

  // Warning-light history
  if (a.io43 != null && a.io43 > 0) {
    checks.push({ icon: '📋', title: 'Warning-Light History', verdict: `${a.io43} km driven with light on`, detail: 'The vehicle has previously been driven with the check-engine light on.', level: 'warn' });
  }

  // canonical dismiss identity (shared across tabs) — key + defining value
  attachAckSig(checks, a);
  return checks;
}

// ---- Current fuel level dial ----
const fuelColor = (pct) => (pct < 10 ? '#ef4444' : pct < 25 ? '#f59e0b' : '#22c55e');

const FuelDial = ({ pct }) => {
  const angle = -90 + (Math.min(pct, 100) / 100) * 180;
  return (
    <svg width="130" height="80" viewBox="0 0 100 62">
      <path d="M 10 55 A 40 40 0 0 1 90 55" fill="none" stroke="#e5e7eb" strokeWidth="9" strokeLinecap="round" />
      <path d="M 10 55 A 40 40 0 0 1 30 20" fill="none" stroke="#ef4444" strokeWidth="9" strokeLinecap="round" />
      <path d="M 30 20 A 40 40 0 0 1 50 15" fill="none" stroke="#f59e0b" strokeWidth="9" strokeLinecap="round" />
      <path d="M 50 15 A 40 40 0 0 1 90 55" fill="none" stroke="#22c55e" strokeWidth="9" strokeLinecap="round" />
      <g transform={`rotate(${angle} 50 55)`}>
        <line x1="50" y1="55" x2="50" y2="20" stroke="#0f172a" strokeWidth="3.5" strokeLinecap="round" />
      </g>
      <circle cx="50" cy="55" r="5" fill="#0f172a" />
      <text x="14" y="61" fontSize="7" fill="#94a3b8">E</text>
      <text x="82" y="61" fontSize="7" fill="#94a3b8">F</text>
    </svg>
  );
};

const FUEL_TYPE_NAMES = { 0: 'Not reported', 1: 'Gasoline', 2: 'Methanol', 3: 'Ethanol', 4: 'Diesel', 5: 'LPG', 6: 'CNG', 7: 'Propane', 8: 'Electric', 9: 'Hybrid gasoline', 17: 'Hybrid diesel' };

const FuelSpotlight = ({ a }) => {
  if (a.io48 == null) return null;
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '16px 24px', marginBottom: '24px', flexWrap: 'wrap' }}>
      <FuelDial pct={a.io48} />
      <div>
        <div className="text-muted" style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>⛽ Current Fuel Level</div>
        <div style={{ fontFamily: 'Poppins, sans-serif', fontSize: '34px', fontWeight: 700, color: fuelColor(a.io48), lineHeight: 1.1 }}>{a.io48}%</div>
        <div className="text-muted" style={{ fontSize: '13px' }}>
          {a.io48 < 10 ? 'Critical — nearly empty' : a.io48 < 25 ? 'Low — plan a fuel stop' : 'Fuel level OK'}
        </div>
      </div>
      <div className="text-muted" style={{ marginLeft: 'auto', fontSize: '13px', textAlign: 'right' }}>
        {a.io759 != null && <div>Fuel type: <b>{FUEL_TYPE_NAMES[a.io759] || `Type ${a.io759}`}</b></div>}
        {a.io12 != null && <div>Lifetime used: <b>{(a.io12 / 10).toFixed(1)} L</b></div>}
      </div>
    </div>
  );
};

const HealthCard = ({ c, resolved, rd, onResolve, onUndo }) => {
  // resolved cards read like a normal healthy card ("everything OK")
  const col = resolved ? HEALTH_COLORS.good : HEALTH_COLORS[c.level];
  const canResolve = (c.level === 'bad' || c.level === 'warn');
  const verdict = resolved && rd ? rd.verdict : c.verdict;
  const detail = resolved && rd ? rd.detail : c.detail;
  // The Check-Engine card can carry the actual fault codes — tap the card to
  // drop the list down.
  const hasCodes = !resolved && Array.isArray(c.codes) && c.codes.length > 0;
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={hasCodes ? () => { (open ? playClose : playOpen)(); setOpen((o) => !o); } : undefined}
      style={{
        background: col.bg, borderLeft: `4px solid ${col.border}`, borderRadius: '10px',
        padding: '14px 16px', boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
        cursor: hasCodes ? 'pointer' : 'default',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <span style={{ fontSize: '16px' }}>{resolved ? '✅' : c.icon}</span>
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{c.title}</span>
        {hasCodes && <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#b91c1c', fontWeight: 700 }}>{open ? '▲' : `▼ ${c.codes.length}`}</span>}
      </div>
      <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: col.text, marginBottom: '2px' }}>{verdict}</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>{detail}</div>
      {hasCodes && open && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {c.codes.map((x) => (
            <div key={x.code} style={{ background: 'white', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 10px' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#b91c1c', fontSize: '13px' }}>{x.code}</span>
              <span style={{ fontSize: '12px', color: '#475569' }}> — {x.meaning}</span>
            </div>
          ))}
        </div>
      )}
      {canResolve && !resolved && (
        <button onClick={(e) => { e.stopPropagation(); onResolve(); }} style={{ marginTop: '8px', background: 'white', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>✓ Mark resolved</button>
      )}
      {resolved && (
        <button onClick={(e) => { e.stopPropagation(); onUndo(); }} style={{ marginTop: '8px', background: 'transparent', color: '#94a3b8', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>Undo</button>
      )}
    </div>
  );
};

export default function AnalyticsPage() {
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [ackTick, setAckTick] = useState(0); // re-render after resolve/undo

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [devs, poss] = await Promise.all([getDevices(), getPositions()]);
        if (cancelled) return;
        mergeServerAcks(devs); // sync resolves made on other profiles/tabs
        setDevices(devs);
        setPositions(poss);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const position = positions.find((p) => p.deviceId === selectedId);
  const device = devices.find((d) => d.id === selectedId);

  // Translate every attribute the device sent into plain language, grouped
  const groups = {};
  if (position) {
    // when check-engine is marked resolved, show the raw fault-code readings as
    // cleared too, so the whole page stays in harmony with the health card.
    const ceResolved = checkEngineResolved(selectedId, position.attributes);
    Object.entries(position.attributes || {}).forEach(([key, value]) => {
      let v = value;
      if (ceResolved) {
        if (key === 'io30') v = 0;
        else if (/^(dtcs|dtc|io281|faultCodes|troubleCodes)$/i.test(key)) v = '—';
      }
      const d = describeAttribute(key, v);
      if (!d) return;
      (groups[d.group] = groups[d.group] || []).push(d);
    });
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>📈 Vehicle Analytics</h1>
        <p>Every reading your tracker sends, translated into plain language. Updates every 10 seconds.</p>
      </div>

      {error && <p style={{ color: '#dc2626' }}>🔴 {error}</p>}

      {/* vehicle selector */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {devices.map((d) => (
          <button
            key={d.id}
            className={`seg-frost ${selectedId === d.id ? 'on' : ''}`}
            onClick={() => { (selectedId === d.id ? playClose : playOpen)(); setSelectedId(selectedId === d.id ? null : d.id); }}
            style={{
              padding: '8px 18px', borderRadius: '999px', fontWeight: 600, fontSize: '13px',
              border: '1px solid ' + (selectedId === d.id ? 'transparent' : '#d1d5db'),
              background: selectedId === d.id ? 'linear-gradient(135deg, #f97316, #f4511e)' : 'white',
              color: selectedId === d.id ? 'white' : '#374151',
              cursor: 'pointer',
            }}
          >
            {(d.attributes || {}).displayName ? (
              <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
                <span>{d.attributes.displayName}</span>
                <span style={{ fontSize: '10px', fontWeight: 500, opacity: 0.7 }}>#{d.name}</span>
              </span>
            ) : d.name}
          </button>
        ))}
      </div>

      {device && position && (
        <p className="text-sm" style={{ marginBottom: '16px' }}>
          {position.attributes && position.attributes.vin && (
            <>VIN: <b style={{ letterSpacing: '0.5px' }}>{position.attributes.vin}</b> · </>
          )}
          Last report: {new Date(position.fixTime).toLocaleString()} · Status: {device.status}
        </p>
      )}

      {!selectedId && !error && devices.length > 0 && (
        <p className="text-muted">Click a vehicle above to see its fuel, health, and full readings — click it again to close.</p>
      )}
      {selectedId && !position && !error && <p>Waiting for data…</p>}

      {position && (
        <>
          <FuelSpotlight a={position.attributes || {}} />

          <h2 className="reading-group-title" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '17px', margin: '4px 0 12px' }}>
            🩺 Vehicle Health (OBD-II)
          </h2>
          <div className="grid grid-3" style={{ marginBottom: '28px' }}>
            {assessHealth(position.attributes).map((c) => {
              const resolved = (c.level === 'bad' || c.level === 'warn') && c.ackKey && isAcked(selectedId, c.ackKey, c.ackSig);
              const rd = resolved ? resolvedDisplay(c.ackKey) : undefined;
              if (resolved && rd === null) return null; // no healthy-state card (e.g. warning-light history)
              return (
                <HealthCard key={c.title} c={c} resolved={resolved} rd={rd}
                  onResolve={() => { ackHealth(selectedId, c.ackKey, c.ackSig); setAckTick((n) => n + 1); }}
                  onUndo={() => { unackHealth(selectedId, c.ackKey); setAckTick((n) => n + 1); }} />
              );
            })}
          </div>

          <h2 className="reading-group-title" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '17px', margin: '4px 0 12px' }}>
            📊 All Readings
          </h2>
        </>
      )}

      <div className="grid grid-3">
        {GROUP_ORDER.filter((g) => groups[g] && groups[g].length).map((g) => (
          <div className="card" key={g} style={{ padding: '18px' }}>
            <div className="reading-group-head" style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
              paddingBottom: '10px',
            }}>
              <span style={{ fontSize: '18px' }}>{GROUP_ICONS[g]}</span>
              <h2 className="reading-group-title" style={{ margin: 0, fontSize: '15px', fontFamily: 'Poppins, sans-serif' }}>{g}</h2>
            </div>
            {groups[g].map((item) => (
              <div key={item.raw} className="reading-row" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: '12px', padding: '6px 0', fontSize: '13px',
              }}>
                <span className="reading-label">{item.label}</span>
                <span className="reading-value" style={{ whiteSpace: 'nowrap' }}>{item.value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
